// Typed client for every browser endpoint of docs/contracts.md. Adds the
// Supabase bearer token to each call and turns error envelopes into ApiError.
import { ERROR_CODES } from '@kima-hub/contracts';
import type {
  DeleteMeetingResponse,
  ErrorBody,
  ErrorCode,
  MeetingListResponse,
  Page,
  PresenceResponse,
  RecordingStartResponse,
  TokenRequest,
  TokenResponse,
} from '@kima-hub/contracts';
import { COPY } from '../copy.js';
import type { WebMeetingDetail, WebSearchHit, WebSegment } from './types.js';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'network',
    readonly status: number,
    /** pt-BR, safe to show. */
    readonly userMessage: string,
  ) {
    super(`${code} (${status})`);
    this.name = 'ApiError';
  }
}

export function isApiError(err: unknown, code?: ApiError['code']): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}

/** pt-BR text for any thrown value. */
export function userMessage(err: unknown): string {
  return err instanceof ApiError ? err.userMessage : COPY.unexpected;
}

export function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken: () => Promise<string | null>;
  fetch?: typeof fetch;
  /** Called on 401 unauthenticated and 403 not_staff before the error is thrown. */
  onAuthFailure?: (err: ApiError) => void;
}

export interface PageParams {
  limit?: number;
  cursor?: string | null;
}

export interface ApiClient {
  token(req: TokenRequest, signal?: AbortSignal): Promise<TokenResponse>;
  presence(signal?: AbortSignal): Promise<PresenceResponse>;
  meetings(page?: PageParams, signal?: AbortSignal): Promise<MeetingListResponse>;
  meeting(id: string, signal?: AbortSignal): Promise<WebMeetingDetail>;
  segments(id: string, page?: PageParams, signal?: AbortSignal): Promise<Page<WebSegment>>;
  transcriptText(meetingId: string, signal?: AbortSignal): Promise<{ filename: string; blob: Blob }>;
  search(q: string, page?: PageParams, signal?: AbortSignal): Promise<Page<WebSearchHit>>;
  deleteMeeting(id: string): Promise<DeleteMeetingResponse>;
  /** "Gravar": 200 with the outcome for any press on a live meeting the caller is in. */
  startRecording(meetingId: string): Promise<RecordingStartResponse>;
}

function isErrorBody(body: unknown): body is ErrorBody {
  if (typeof body !== 'object' || body === null) return false;
  const error = (body as { error?: unknown }).error;
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { code?: unknown }).code === 'string' &&
    typeof (error as { message?: unknown }).message === 'string'
  );
}

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** Maps a bare status (no envelope, e.g. a proxy error page) to the closest contract code. */
function statusCode(status: number): ErrorCode {
  const match = (Object.entries(ERROR_CODES) as [ErrorCode, number][]).find(([, s]) => s === status);
  return match ? match[0] : status >= 500 ? 'internal' : 'invalid_request';
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const base = options.baseUrl.replace(/\/+$/, '');
  const doFetch: typeof fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  async function responseError(res: Response): Promise<never> {
    const parsed: unknown = await res.json().catch(() => null);
    const err = isErrorBody(parsed)
      ? new ApiError(parsed.error.code, res.status, parsed.error.message)
      : new ApiError(statusCode(res.status), res.status, COPY.unexpected);
    if (err.code === 'unauthenticated' || err.code === 'not_staff') options.onAuthFailure?.(err);
    throw err;
  }

  async function authenticatedFetch(method: string, path: string, accept: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const token = await options.getAccessToken();
    const headers: Record<string, string> = { Accept: accept };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let res: Response;
    try {
      res = await doFetch(`${base}${path}`, {
        method,
        headers,
        body: body === undefined ? null : JSON.stringify(body),
        signal: signal ?? null,
      });
    } catch (err) {
      if (signal?.aborted || isAbort(err)) throw err;
      throw new ApiError('network', 0, COPY.network);
    }
    return res;
  }

  async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const res = await authenticatedFetch(method, path, 'application/json', body, signal);
    if (!res.ok) return responseError(res);
    return (await res.json().catch(() => null)) as T;
  }

  function attachmentFilename(disposition: string | null): string {
    if (!disposition) return 'transcricao.txt';
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encoded) {
      try {
        return decodeURIComponent(encoded);
      } catch {
        return 'transcricao.txt';
      }
    }
    return disposition.match(/filename="([^"]+)"/i)?.[1] ?? disposition.match(/filename=([^;\s]+)/i)?.[1] ?? 'transcricao.txt';
  }

  const id = (raw: string) => encodeURIComponent(raw);

  return {
    token: (req, signal) => request('POST', '/token', req, signal),
    presence: (signal) => request('GET', '/rooms/presence', undefined, signal),
    meetings: (page = {}, signal) => request('GET', `/meetings${query({ ...page })}`, undefined, signal),
    meeting: (meetingId, signal) => request('GET', `/meetings/${id(meetingId)}`, undefined, signal),
    segments: (meetingId, page = {}, signal) =>
      request('GET', `/meetings/${id(meetingId)}/segments${query({ ...page })}`, undefined, signal),
    transcriptText: async (meetingId, signal) => {
      const res = await authenticatedFetch('GET', `/meetings/${id(meetingId)}/transcript.txt`, 'text/plain', undefined, signal);
      if (!res.ok) return responseError(res);
      return { filename: attachmentFilename(res.headers.get('Content-Disposition')), blob: await res.blob() };
    },
    search: (q, page = {}, signal) => request('GET', `/search${query({ q, ...page })}`, undefined, signal),
    deleteMeeting: (meetingId) => request('DELETE', `/meetings/${id(meetingId)}`),
    startRecording: (meetingId) => request('POST', `/meetings/${id(meetingId)}/recording/start`),
  };
}
